import {
    ColumnDef,
    ColumnFiltersState,
    OnChangeFn,
    PaginationState,
    Row,
    Table,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from '../../../useTranslation'
import { TableHeader } from './TableHeader'
import { TableRow } from './TableRow'

export type Pagination = {
    pageCount: number
    pagination: PaginationState
    onPaginationChange: OnChangeFn<PaginationState>
    manualPagination: boolean
}

type Props<T> = {
    columns: Array<ColumnDef<T, unknown>>
    tableData: T[]
    pagination?: Pagination
    rowClass?: string
    headerClass?: string
    columnClass?: string
    getRowCanExpand?: (row: Row<T>) => boolean
    renderSubComponent?: any
}

function TableBase<T>({ columns, tableData, pagination, rowClass, headerClass, columnClass, getRowCanExpand, renderSubComponent }: Props<T>) {
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

    const table = useReactTable<T>({
        data: [...tableData],
        columns,
        getCoreRowModel: getCoreRowModel(),
        pageCount: pagination?.pageCount,
        state: {
            pagination: pagination?.pagination,
            columnFilters,
        },
        manualPagination: pagination?.manualPagination,
        onPaginationChange: pagination?.onPaginationChange,
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        getRowCanExpand,
        getExpandedRowModel: getExpandedRowModel(),
    })

    const rows = table.getRowModel().rows

    return (
        <>
            <table className="w-full table-auto paddingBetweenCols">
                <TableHeader table={table} headerClass={headerClass as string} />
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <TableRow
                            row={row}
                            isLastRow={rowIndex === rows.length - 1}
                            key={rowIndex}
                            rowClass={rowClass}
                            columnClass={columnClass}
                            renderSubComponent={renderSubComponent}
                        />
                    ))}
                </tbody>
            </table>
            {pagination && <TablePagination table={table} />}
        </>
    )
}

function TablePagination<T>(props: { table: Table<T> }) {
    const { table } = props
    const { t } = useTranslation()

    return (
        <div className="flex items-center justify-center gap-2 pt-5">
            <button
                className="font-bold text-base py-1.5 px-2 outline-hidden leading-none max-tablet:text-sm max-tablet:pt-2.5 max-tablet:pb-2 max-lg:px-5 max-lg:min-w-40 text-white bg-custom_blue hover:bg-blue-800 cursor-pointer"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
            >
                {'<<'}
            </button>
            <button
                className="font-bold text-base py-1.5 px-2 outline-hidden leading-none max-tablet:text-sm max-tablet:pt-2.5 max-tablet:pb-2 max-lg:px-5 max-lg:min-w-40 text-white bg-custom_blue hover:bg-blue-800 cursor-pointer"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
            >
                {'<'}
            </button>
            <span className="flex items-center gap-1 text-black text-sm">
                {t('page')} {table.getState().pagination.pageIndex + 1} {t('of')} {table.getPageCount()}
            </span>
            <button
                className="font-bold text-base py-1.5 px-2 outline-hidden leading-none max-tablet:text-sm max-tablet:pt-2.5 max-tablet:pb-2 max-lg:px-5 max-lg:min-w-40 text-white bg-custom_blue hover:bg-blue-800 cursor-pointer"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
            >
                {'>'}
            </button>
            <button
                className="font-bold text-base py-1.5 px-2 outline-hidden leading-none max-tablet:text-sm max-tablet:pt-2.5 max-tablet:pb-2 max-lg:px-5 max-lg:min-w-40 text-white bg-custom_blue hover:bg-blue-800 cursor-pointer"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
            >
                {'>>'}
            </button>

            <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                    table.setPageSize(Number(e.target.value))
                }}
                className="text-black text-sm outline-hidden"
            >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                        {t('show')} {pageSize}
                    </option>
                ))}
            </select>
        </div>
    )
}

export default TableBase
